import { useState } from 'react';

import { useDeleteEvent } from '~entities/events/api/delete-event';
import { useGetEventStudentCount } from '~entities/events/api/get-event-student-count';
import type { EventDetails } from '~entities/events/modal/event';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/shadcn/ui/dialog';
import { Label } from '~/shared/shadcn/ui/label';
import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';

interface DeleteEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedEvent: EventDetails | null;
  onClose: () => void;
  isBulkDelete?: boolean;
  onDelete?: () => void;
}

export const DeleteEventDialog = ({
  open,
  onOpenChange,
  selectedEvent,
  onClose,
  isBulkDelete = false,
  onDelete,
}: DeleteEventDialogProps) => {
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const { data: eventStudentCount } = useGetEventStudentCount({
    eventId: selectedEvent?._id ?? '',
    enabled: !!selectedEvent && !isBulkDelete,
  });

  const deleteMutation = useDeleteEvent();

  const handleClose = () => {
    setDeleteConfirmText('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {isBulkDelete ? 'Events' : 'Event'}</DialogTitle>
        </DialogHeader>

        {(!eventStudentCount ||
          eventStudentCount.count === 0 ||
          isBulkDelete) && (
          <>
            <DialogDescription>
              {isBulkDelete
                ? 'Are you sure you want to delete these events? This action cannot be undone.'
                : 'Are you sure you want to delete this event? This action cannot be undone.'}
            </DialogDescription>
            <div className='mt-4'>
              <Label htmlFor='confirmDelete'>
                Type &quot;delete&quot; to confirm:
              </Label>
              <Input
                id='confirmDelete'
                value={deleteConfirmText}
                onChange={(e) => {
                  setDeleteConfirmText(e.target.value);
                }}
                className='mt-2'
              />
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant='destructive'
                disabled={deleteConfirmText !== 'delete'}
                onClick={() => {
                  if (isBulkDelete && onDelete) {
                    onDelete();
                  } else if (selectedEvent) {
                    deleteMutation.mutate({ eventId: selectedEvent._id });
                  }
                  handleClose();
                }}
              >
                {isBulkDelete ? 'Delete Events' : 'Delete Event'}
              </Button>
            </DialogFooter>
          </>
        )}

        {eventStudentCount?.count !== undefined &&
          eventStudentCount.count > 0 &&
          !isBulkDelete && (
            <>
              <DialogDescription className='text-destructive'>
                This event has {eventStudentCount.count} enrolled{' '}
                {eventStudentCount.count === 1 ? 'student' : 'students'}. Please
                remove all students before deleting the event.
              </DialogDescription>
              <DialogFooter>
                <Button variant='outline' onClick={handleClose}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
      </DialogContent>
    </Dialog>
  );
};
